import { mapActions, mapState } from "vuex";

/**
 * Mixin: manejo del árbol de ubicaciones por empresa y país.
 * - Expone `treeWithChildrens` desde el store.
 * - Provee `loadLocationsTree(companyId, countryId)`.
 * - Incluye `flattenTreeIds(nodes)` para obtener todos los ids del árbol.
 */
export default {
  computed: {
    ...mapState("location", ["treeWithChildrens"]),
  },
  methods: {
    ...mapActions("location", ["getTreeWithChildrens"]),
    ...mapActions("configurableVariables", ["getAllCountriesByCompany"]),

    async loadLocationsTree(companyId, countryId = null) {
      if (!companyId) return;
      await this.getTreeWithChildrens({ company_id: companyId, country_id: countryId || null });
    },

    /**
     * Carga países (si company_id existe) y el árbol filtrado por país opcional.
     * @param {{ company_id: number, country_id?: number|null }} payload
     */
    async loadCompanyGeo(payload) {
      if (!payload || !payload.company_id) return;
      await this.getAllCountriesByCompany(payload.company_id);
      await this.getTreeWithChildrens({ company_id: payload.company_id, country_id: payload.country_id || null });
    },

    flattenTreeIds(nodes) {
      if (!Array.isArray(nodes)) return [];
      const ids = [];
      const stack = nodes.slice();
      while (stack.length) {
        const node = stack.pop();
        if (node && node.id) ids.push(node.id);
        if (node && node.children && node.children.length) {
          for (let i = 0; i < node.children.length; i++) {
            stack.push(node.children[i]);
          }
        }
      }
      return ids;
    },

    /**
     * Encuentra la ubicación de nivel más alto que tenga el usuario asignada
     * @param {Array} tree - Árbol de ubicaciones
     * @param {Array} userLocationIds - IDs de ubicaciones del usuario
     * @returns {number|null} - ID de la ubicación de nivel más alto o null
     */
    findHighestLevelUserLocation(tree, userLocationIds) {
      if (!Array.isArray(tree) || !Array.isArray(userLocationIds) || userLocationIds.length === 0) {
        return null;
      }

      // Función recursiva para buscar en el árbol
      const searchInTree = (nodes, level = 0) => {
        let highestLevel = -1;
        let selectedId = null;

        for (const node of nodes) {
          if (node && node.id) {
            // Si el nodo actual está en las ubicaciones del usuario
            if (userLocationIds.includes(node.id)) {
              if (level > highestLevel) {
                highestLevel = level;
                selectedId = node.id;
              }
            }

            // Buscar recursivamente en los hijos
            if (node.children && node.children.length > 0) {
              const childResult = searchInTree(node.children, level + 1);
              if (childResult.level > highestLevel) {
                highestLevel = childResult.level;
                selectedId = childResult.id;
              }
            }
          }
        }

        return { level: highestLevel, id: selectedId };
      };

      const result = searchInTree(tree);
      return result.id;
    },

    /**
     * Carga el árbol de ubicaciones y selecciona automáticamente la ubicación de nivel más alto del usuario
     * @param {number} companyId - ID de la empresa
     * @param {Array} userLocationIds - IDs de ubicaciones del usuario
     * @param {number|null} countryId - ID del país (opcional)
     * @param {number|null} userCompanyId - ID de la empresa del usuario (opcional)
     * @returns {Promise<number|null>} - ID de la ubicación seleccionada o null
     */
    async loadLocationsTreeWithAutoSelect(companyId, userLocationIds, countryId = null, userCompanyId = null) {
      if (!companyId) return null;
      
      // Solo aplicar selección automática si:
      // 1. userCompanyId no es null/undefined
      // 2. La empresa seleccionada es la del usuario
      if (!userCompanyId || companyId !== userCompanyId) {
        await this.getTreeWithChildrens({ company_id: companyId, country_id: countryId || null });
        return null;
      }
      
      await this.getTreeWithChildrens({ company_id: companyId, country_id: countryId || null });
      
      // Buscar la ubicación de nivel más alto del usuario
      const selectedLocationId = this.findHighestLevelUserLocation(this.treeWithChildrens, userLocationIds);
      
      return selectedLocationId;
    },

    /**
     * Encuentra todas las ubicaciones del usuario que estén disponibles en el árbol
     * @param {Array} tree - Árbol de ubicaciones
     * @param {Array} userLocationIds - IDs de ubicaciones del usuario
     * @returns {Array} - Array de IDs de ubicaciones disponibles del usuario
     */
    findUserAvailableLocations(tree, userLocationIds) {
      if (!Array.isArray(tree) || !Array.isArray(userLocationIds) || userLocationIds.length === 0) {
        return [];
      }

      const availableLocations = [];
      
      // Función recursiva para buscar en el árbol
      const searchInTree = (nodes) => {
        for (const node of nodes) {
          if (node && node.id) {
            // Si el nodo actual está en las ubicaciones del usuario
            if (userLocationIds.includes(node.id)) {
              availableLocations.push(node.id);
            }

            // Buscar recursivamente en los hijos
            if (node.children && node.children.length > 0) {
              searchInTree(node.children);
            }
          }
        }
      };

      searchInTree(tree);
      return availableLocations;
    },

    /**
     * Carga el árbol de ubicaciones y selecciona automáticamente todas las ubicaciones del usuario
     * @param {number} companyId - ID de la empresa
     * @param {Array} userLocationIds - IDs de ubicaciones del usuario
     * @param {number|null} countryId - ID del país (opcional)
     * @param {number|null} userCompanyId - ID de la empresa del usuario (opcional)
     * @returns {Promise<Array>} - Array de IDs de ubicaciones seleccionadas
     */
    async loadLocationsTreeWithMultipleAutoSelect(companyId, userLocationIds, countryId = null, userCompanyId = null) {
      if (!companyId) return [];
      
      // Solo aplicar selección automática si:
      // 1. userCompanyId no es null/undefined
      // 2. La empresa seleccionada es la del usuario
      if (!userCompanyId || companyId !== userCompanyId) {
        await this.getTreeWithChildrens({ company_id: companyId, country_id: countryId || null });
        return [];
      }
      
      await this.getTreeWithChildrens({ company_id: companyId, country_id: countryId || null });
      
      // Buscar todas las ubicaciones del usuario que estén disponibles
      const selectedLocationIds = this.findUserAvailableLocations(this.treeWithChildrens, userLocationIds);
      
      return selectedLocationIds;
    },

    /**
     * Carga el árbol de ubicaciones y selecciona automáticamente las ubicaciones del usuario
     * Detecta automáticamente si debe usar selección única o múltiple
     * @param {number} companyId - ID de la empresa
     * @param {Array} userLocationIds - IDs de ubicaciones del usuario
     * @param {number|null} countryId - ID del país (opcional)
     * @param {boolean} isMultiple - Si es true, selecciona todas las ubicaciones del usuario; si es false, selecciona solo la de nivel más alto
     * @param {number|null} userCompanyId - ID de la empresa del usuario (opcional)
     * @returns {Promise<number|Array|null>} - ID único, array de IDs, o null según el tipo de selección
     */
    async loadLocationsTreeWithSmartAutoSelect(companyId, userLocationIds, countryId = null, isMultiple = false, userCompanyId = null) {
      if (!companyId) return isMultiple ? [] : null;
      
      // Solo aplicar selección automática si:
      // 1. userCompanyId no es null/undefined
      // 2. La empresa seleccionada es la del usuario
      if (!userCompanyId || companyId !== userCompanyId) {
        await this.getTreeWithChildrens({ company_id: companyId, country_id: countryId || null });
        return isMultiple ? [] : null;
      }
      
      await this.getTreeWithChildrens({ company_id: companyId, country_id: countryId || null });
      
      if (isMultiple) {
        // Selección múltiple: todas las ubicaciones del usuario
        return this.findUserAvailableLocations(this.treeWithChildrens, userLocationIds);
      } else {
        // Selección única: ubicación de nivel más alto
        return this.findHighestLevelUserLocation(this.treeWithChildrens, userLocationIds);
      }
    },
  },
};


